import { Injectable, signal } from '@angular/core';
import { storyStepDemoIdFromKey } from './story-step-key';

export interface TopicItem {
  id: string;
  label: string;
  draft: string;
}

interface SuggestionSessionState {
  /** All chips — always visible; never cleared on selection. */
  topics: TopicItem[];
  /** Step key for the currently applied suggestions set. */
  activeStepKey: string;
  /** ID of the currently selected topic chip (highlighted). */
  selectedTopicId: string | null;
  /** True while the typewriter animation is running. */
  generating: boolean;
  loading: boolean;
  error: string | null;
}

/** djb2 hash for deterministic seeding */
function djb2(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
    h = h >>> 0;
  }
  return h;
}

const FALLBACK_TOPICS: TopicItem[] = [
  {
    id: 'f-savings',
    label: 'Review savings goal',
    draft: "I wanted to touch base on your savings goal. Based on your current progress, you're tracking well and I'd love to walk through where things stand and discuss any adjustments.",
  },
  {
    id: 'f-401k',
    label: 'Discuss 401(k)',
    draft: "I'd like to go over your 401(k) contribution rate. We have a few options to consider depending on your comfort level, and I can show you the projected impact on your take-home pay.",
  },
  {
    id: 'f-emergency',
    label: 'Emergency fund',
    draft: "I wanted to discuss setting up a dedicated emergency fund. A good target is 3–6 months of expenses in a high-yield account, and we can automate contributions to make it easy.",
  },
  {
    id: 'f-review',
    label: 'Schedule a review',
    draft: "I'd like to schedule our next portfolio review. It would be a good opportunity to revisit your goals, check in on performance, and make sure your plan still reflects your priorities.",
  },
  {
    id: 'f-beneficiary',
    label: 'Beneficiary update',
    draft: "I wanted to follow up on your beneficiary designations. It's worth reviewing these periodically to make sure everything is up to date, especially after any major life changes.",
  },
];

function buildFallbackTopics(sessionId: string): TopicItem[] {
  const seed = djb2(sessionId);
  const result: TopicItem[] = [];
  const used = new Set<number>();
  for (let i = 0; i < 3; i++) {
    let idx = (seed + i * 7) % FALLBACK_TOPICS.length;
    while (used.has(idx)) {
      idx = (idx + 1) % FALLBACK_TOPICS.length;
    }
    used.add(idx);
    result.push(FALLBACK_TOPICS[idx]);
  }
  return result;
}

type RawTopic =
  | string
  | { id?: string; label?: string; text?: string; draft?: string; response?: string };

function normalizeTopics(raw: unknown): TopicItem[] {
  if (!Array.isArray(raw)) return [];
  return (raw as RawTopic[])
    .filter((item) => {
      if (typeof item === 'string') return item.trim().length > 0;
      return item && typeof item === 'object';
    })
    .map((item, idx): TopicItem | null => {
      if (typeof item === 'string') {
        const text = item.trim();
        return text ? { id: `t-${idx}`, label: text, draft: text } : null;
      }
      const label = (item.label ?? item.text ?? '').trim();
      const draft = (item.draft ?? item.response ?? item.text ?? label).trim();
      if (!label) return null;
      return { id: item.id ?? `t-${idx}`, label, draft };
    })
    .filter((t): t is TopicItem => t !== null);
}

const DEFAULT_STATE: SuggestionSessionState = {
  topics: [],
  activeStepKey: '',
  selectedTopicId: null,
  generating: false,
  loading: false,
  error: null,
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

type TextEntry = { id?: string; text: string };

function toTextEntries(value: unknown): TextEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item): TextEntry | null => {
      if (typeof item === 'string') {
        const text = item.trim();
        return text ? { text } : null;
      }
      if (!item || typeof item !== 'object') return null;
      const obj = item as Record<string, unknown>;
      const text = String(obj['text'] ?? obj['label'] ?? obj['title'] ?? '').trim();
      if (!text) return null;
      const idRaw = obj['id'];
      const id = typeof idRaw === 'string' && idRaw.trim() ? idRaw.trim() : undefined;
      return { id, text };
    })
    .filter((entry): entry is TextEntry => entry !== null);
}

type StorySuggestions = {
  topics: TopicItem[];
  source: 'delta' | 'snapshot' | 'legacy' | 'none';
};

function toRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function extractCopilotContainer(
  section: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!section) return undefined;
  return (
    toRecord(section['copilot']) ??
    toRecord(section['fa.chat.thread']) ??
    toRecord(section['fa.chat']) ??
    toRecord(section['assistant.copilot'])
  );
}

function buildTopicsFromLegacyResponses(raw: unknown, idPrefix: string): TopicItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, idx): TopicItem | null => {
      if (typeof item === 'string') {
        const text = item.trim();
        if (!text) return null;
        return {
          id: `${idPrefix}-${idx}`,
          label: text,
          draft: text,
        };
      }
      if (!item || typeof item !== 'object') return null;
      const obj = item as Record<string, unknown>;
      const label = String(obj['text'] ?? obj['label'] ?? obj['title'] ?? '').trim();
      if (!label) return null;
      const draft = String(
        obj['draft'] ?? obj['response'] ?? obj['question'] ?? obj['text'] ?? label
      ).trim();
      return {
        id: String(obj['id'] ?? `${idPrefix}-${idx}`),
        label,
        draft: draft || label,
      };
    })
    .filter((topic): topic is TopicItem => topic !== null);
}

function hasSuggestionKeys(obj: Record<string, unknown> | undefined): boolean {
  if (!obj) return false;
  return (
    'suggestionsChipText' in obj ||
    'suggestedAiQuestions' in obj ||
    'nextSuggestedResponses' in obj ||
    'nextSuggestedTopics' in obj
  );
}

function buildTopicsFromSuggestionObject(
  obj: Record<string, unknown> | undefined,
  idPrefix: string
): TopicItem[] {
  if (!obj) return [];

  const fromNewFields = buildTopicsFromCopilot(
    obj['suggestionsChipText'],
    obj['suggestedAiQuestions'],
    idPrefix
  );
  if (fromNewFields.length > 0) return fromNewFields;

  const fromLegacyResponses = buildTopicsFromLegacyResponses(
    obj['nextSuggestedResponses'],
    idPrefix
  );
  if (fromLegacyResponses.length > 0) return fromLegacyResponses;

  return normalizeTopics(obj['nextSuggestedTopics']).map((topic, idx) => ({
    ...topic,
    id: topic.id || `${idPrefix}-${idx}`,
  }));
}

function buildTopicsFromCopilot(
  chipTextsRaw: unknown,
  aiQuestionsRaw: unknown,
  idPrefix: string
): TopicItem[] {
  const chipEntries = toTextEntries(chipTextsRaw);
  if (chipEntries.length === 0) return [];
  const aiQuestionEntries = toTextEntries(aiQuestionsRaw);
  return chipEntries.map((chip, idx) => {
    const label = chip.text;
    const draft = aiQuestionEntries[idx]?.text ?? label;
    return {
      id: chip.id ?? `${idPrefix}-${idx}`,
      label,
      draft,
    };
  });
}

function extractCopilotSuggestions(raw: unknown, stepKey: string): StorySuggestions {
  if (!raw || typeof raw !== 'object') {
    return { topics: [], source: 'none' };
  }

  const payload = raw as Record<string, unknown>;
  const deltaSection = toRecord(payload['delta']);
  const snapshotSection = toRecord(payload['snapshot']);
  const deltaCopilot = extractCopilotContainer(deltaSection);
  const snapshotCopilot = extractCopilotContainer(snapshotSection);

  const deltaHasSuggestionKeys = hasSuggestionKeys(deltaCopilot) || hasSuggestionKeys(deltaSection);
  if (deltaHasSuggestionKeys) {
    return {
      topics: buildTopicsFromSuggestionObject(
        deltaCopilot ?? deltaSection,
        `copilot-${stepKey}`
      ),
      source: 'delta',
    };
  }

  const snapshotHasSuggestionKeys =
    hasSuggestionKeys(snapshotCopilot) || hasSuggestionKeys(snapshotSection);
  if (snapshotHasSuggestionKeys) {
    return {
      topics: buildTopicsFromSuggestionObject(
        snapshotCopilot ?? snapshotSection,
        `copilot-${stepKey}`
      ),
      source: 'snapshot',
    };
  }

  if ('nextSuggestedTopics' in payload) {
    return {
      topics: normalizeTopics(payload['nextSuggestedTopics']),
      source: 'legacy',
    };
  }

  return { topics: [], source: 'none' };
}

/**
 * Per-session signal store for Smart Compose topic chips.
 * Scoped to My Clients layout (no providedIn).
 */
@Injectable()
export class ClientChatSuggestionsStateService {
  private readonly _state = signal<Record<string, SuggestionSessionState>>({});
  readonly state = this._state.asReadonly();

  private update(sessionId: string, patch: Partial<SuggestionSessionState>): void {
    this._state.update((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] ?? DEFAULT_STATE), ...patch },
    }));
  }

  getState(sessionId: string): SuggestionSessionState {
    return this._state()[sessionId] ?? { ...DEFAULT_STATE };
  }

  applyStepPayload(sessionId: string, raw: unknown, stepKey: string): void {
    const current = this.getState(sessionId);
    if (current.activeStepKey === stepKey) return;

    const extracted = extractCopilotSuggestions(raw, stepKey);
    if (extracted.source !== 'none') {
      if (extracted.topics.length > 0) {
        this.update(sessionId, {
          topics: extracted.topics,
          activeStepKey: stepKey,
          selectedTopicId: null,
          generating: false,
          loading: false,
          error: null,
        });
      } else {
        // Suggestion keys were present for this step, but payload had no usable
        // chips; preserve existing chips and only advance step marker.
        this.update(sessionId, { activeStepKey: stepKey });
      }
      return;
    }

    const prevDemo = storyStepDemoIdFromKey(current.activeStepKey);
    const nextDemo = storyStepDemoIdFromKey(stepKey);
    const enteringStoryOrCrossDemo =
      nextDemo !== null && (prevDemo === null || prevDemo !== nextDemo);
    if (enteringStoryOrCrossDemo) {
      this.update(sessionId, {
        topics: [],
        activeStepKey: stepKey,
        selectedTopicId: null,
        generating: false,
        loading: false,
        error: null,
      });
      return;
    }

    // No suggestion fields for this step: keep current chips unchanged.
    this.update(sessionId, { activeStepKey: stepKey });
  }

  loadFromPayload(sessionId: string, raw: unknown): void {
    const topics = normalizeTopics(raw);
    if (topics.length > 0) {
      this.update(sessionId, {
        topics,
        activeStepKey: '',
        selectedTopicId: null,
        generating: false,
        loading: false,
        error: null,
      });
    } else {
      this.loadFallback(sessionId);
    }
  }

  loadFallback(sessionId: string): void {
    const topics = buildFallbackTopics(sessionId);
    this.update(sessionId, {
      topics,
      activeStepKey: 'fallback',
      selectedTopicId: null,
      generating: false,
      loading: false,
      error: null,
    });
  }

  /**
   * Called when the FA clicks a chip.
   * Highlights the selected chip (chips stay visible), marks generating.
   * Returns the draft text so the caller can run the typewriter.
   */
  selectTopic(sessionId: string, topicId: string): string {
    const s = this.getState(sessionId);
    const topic = s.topics.find((t) => t.id === topicId);
    if (!topic) return '';
    this.update(sessionId, { selectedTopicId: topicId, generating: true });
    return topic.draft;
  }

  /** Called when the typewriter finishes. */
  finishGenerating(sessionId: string): void {
    this.update(sessionId, { generating: false });
  }

  /**
   * Clears the selected chip highlight.
   * Called when the FA erases the entire draft (empty string).
   */
  clearSelection(sessionId: string): void {
    this.update(sessionId, { selectedTopicId: null, generating: false });
  }

  setLoading(sessionId: string, loading: boolean): void {
    this.update(sessionId, { loading });
  }

  setError(sessionId: string, error: string | null): void {
    this.update(sessionId, { error, loading: false });
  }
}
